/**
 * History › Keeper history — built to turn `16b`.
 *
 * A season, not a projection: five manager cards across, two rows, each listing who that GM kept
 * and what it cost him. The drawing puts four things on a card in this order — who, what he
 * plays, what he was charged, and how he was acquired — and nothing else, because the question
 * this tab answers is "who kept whom".
 *
 * THE CHAIN LIVES ON THE PLAYER, NOT THE TABLE. The escalation a player actually ran — 9 → 6 → 3
 * — is genuinely useful and is a per-player fact, so it opens from his row rather than sitting in
 * the grid. A table of chains asks the reader to hold six timelines at once, which is what made
 * the earlier version hard to scan. And it is the chain that HAPPENED, read back out of the
 * keeper archive season by season, not a projection of what the Grid says should happen.
 *
 * Where the charged pick is known it is stated exactly (`6.01 · #51`), resolved by finding the
 * player in that season's draft board. Where it is not — a farm keeper who never occupied a draft
 * pick, or a season the board is missing — the row says `Round 6` and `pick not logged` rather
 * than picking one of the GM's two sixths and hoping.
 */

import { useMemo, useState } from 'react';
import keeperHistory from '../data/keeper-history.json';
import draftHistory from '../data/draft-history.json';
import { managerName, managerShort } from '../domain/identity.js';
import { escalationChain } from '../domain/grid.js';
import { GMS } from '../store/session.js';
import { GmBadge } from './GmBadge.js';
import { NflMark } from './NflChip.js';
import { Linkify } from './RuleText.js';

interface Keeper {
  gm: string | null; player: string; round: number; farm?: boolean;
  cost?: number; acquisition?: string; sleeper?: boolean; verified?: boolean;
}
interface KeeperSeason { season: number; keepers: Keeper[] }
interface DraftPick { overall: number; round: number; pick: number; gm: string; player: string; keeper?: boolean }
interface DraftSeason { season: number; picks: DraftPick[] }

const KEEPERS = (keeperHistory as unknown as { seasons: KeeperSeason[]; note?: string }).seasons;
const DRAFTS = (draftHistory as unknown as { seasons: DraftSeason[] }).seasons;
const SEASONS = [...KEEPERS].sort((a, b) => b.season - a.season);

const norm = (s: string) => s.normalize('NFKD').toLowerCase().replace(/[^a-z ]/g, '').trim();

/** The pick a keeper actually occupied, where that season's board records him. */
function pickFor(season: number, player: string): { code: string; overall: number } | null {
  const board = DRAFTS.find((d) => d.season === season);
  const hit = board?.picks.find((p) => norm(p.player) === norm(player));
  if (!hit) return null;
  return { code: `${hit.round}.${String(hit.pick).padStart(2, '0')}`, overall: hit.overall };
}

export function HistoryKeepers() {
  const [season, setSeason] = useState(SEASONS[0]?.season ?? 2025);
  const [who, setWho] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const file = SEASONS.find((s) => s.season === season);

  /** One card per manager, in roster order, with the entries the archive has for him. */
  const cards = useMemo(() => {
    const byGm = new Map<string, Keeper[]>();
    for (const k of file?.keepers ?? []) {
      const gm = (k.gm ?? '').toUpperCase();
      if (!gm) continue;
      (byGm.get(gm) ?? byGm.set(gm, []).get(gm)!).push(k);
    }
    return GMS
      .filter((g) => !who || g === who)
      .map((g) => ({
        gm: g,
        kept: (byGm.get(g) ?? []).sort((a, b) => a.round - b.round),
        read: byGm.has(g),
      }));
  }, [file, who]);

  /**
   * What a player was charged, season by season, for as long as the archive has him — and what
   * the Grid says is left. Keeper markers only exist from 2010 on, so a run that starts at the
   * edge of the record is a floor.
   */
  const chainFor = (player: string) => {
    const all = SEASONS
      .map((s) => ({ season: s.season, k: s.keepers.find((x) => norm(x.player) === norm(player)) }))
      .filter((x) => x.k)
      .sort((a, b) => a.season - b.season)
      .map((x) => ({ season: x.season, round: x.k!.round }));

    /*
     * The CURRENT run, not every season he was ever kept. A player dropped and re-drafted starts
     * a new clock, and stitching two separate runs together would say he is out of years when
     * the Grid gives him three.
     */
    const runs: typeof all = [];
    for (let i = all.length - 1; i >= 0; i -= 1) {
      const next = runs[0];
      if (next && next.season - all[i]!.season !== 1) break;
      runs.unshift(all[i]!);
    }

    /*
     * Years left runs forward from what he was charged LAST, not from where the run started: the
     * round he occupies is the value he carries into next season, so the Grid's own chain from
     * that round IS the answer. Counting down from the first round of the run instead would have
     * told a GM his 4th-round keeper was finished when the Grid gives him one more year.
     */
    const first = runs[0];
    const last = runs[runs.length - 1];
    const left = last ? escalationChain(last.round).length : 0;
    const earliest = Math.min(...SEASONS.map((s) => s.season));
    return { runs, left, floor: first?.season === earliest, broken: runs.length < all.length };
  };

  const read = cards.filter((c) => c.read).length;

  return (
    <div className="app">
      <Linkify>
        <header className="pagehead">
          <div>
            <h1>Keeper history</h1>
            <p className="sub">Every keeper declared, by season and manager</p>
          </div>
          <div className="filters">
            <label className="yearpick">
              <span className="mut">Season</span>
              <select value={season} onChange={(e) => { setSeason(Number(e.target.value)); setOpen(null); }}>
                {SEASONS.map((s) => <option key={s.season} value={s.season}>{s.season}</option>)}
              </select>
            </label>
            <label className="yearpick">
              <span className="mut">Manager</span>
              <select value={who} onChange={(e) => setWho(e.target.value)}>
                <option value="">All managers</option>
                {GMS.map((g) => <option key={g} value={g}>{managerName(g)}</option>)}
              </select>
            </label>
          </div>
        </header>

        <div className="keepercards">
          {cards.map((c) => (
            <section className="card keepercard" key={c.gm}>
              <div className="kchead">
                <GmBadge gm={c.gm} />
                <b>{managerShort(c.gm)}</b>
                <span className="mut">
                  {c.read ? `${c.kept.length} kept` : '—'}
                </span>
              </div>
              <div className="kcbody">
                {!c.read && <p className="notread">not read</p>}
                {c.kept.map((k) => {
                  const at = pickFor(season, k.player);
                  const id = `${c.gm}:${k.player}`;
                  const chain = open === id ? chainFor(k.player) : null;
                  return (
                    <div className="kentry" key={id}>
                      <button type="button" className="kname"
                        onClick={() => setOpen(open === id ? null : id)}
                        title="Show what he cost, season by season">
                        <span className="pname">{k.player}</span>
                        {k.farm && <span className="chip farmchip">Farm</span>}
                        {k.sleeper && <span className="chip sleeperchip">Sleeper</span>}
                      </button>
                      <div className="kmeta">
                        <NflMark player={k.player} />
                        {at
                          ? <span className="kcode">{at.code} <span className="mut">#{at.overall}</span></span>
                          : (
                            <span className="kcode">
                              Round {k.round} <span className="faint">pick not logged</span>
                            </span>
                          )}
                      </div>
                      {chain && (
                        <div className="kchain">
                          <span className="chainline">
                            {chain.runs.map((r, i) => (
                              <span key={r.season}>
                                {i > 0 && <span className="arrow"> → </span>}
                                <span title={`${r.season}`}>{r.round}</span>
                              </span>
                            ))}
                          </span>
                          <span className="mut">
                            {chain.runs.length} season{chain.runs.length === 1 ? '' : 's'}
                            {chain.left > 0
                              ? ` · ${chain.left} more year${chain.left === 1 ? '' : 's'}`
                              : ' · cannot be kept again'}
                            {chain.broken ? ' · earlier run ended when he was dropped' : ''}
                            {chain.floor ? ' · may start earlier than the archive' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {c.read && c.kept.length === 0 && <p className="notread">kept nobody</p>}
              </div>
            </section>
          ))}
        </div>

        <section className="card chaincallout">
          <p>
            <b>The chain lives on the player, not the table.</b> Opening a name shows the
            escalation he actually ran and how many keeper years the Grid leaves him. Keeping it
            behind a click means this page answers &ldquo;who kept whom&rdquo; without asking
            anybody to decode six chains at once.
          </p>
        </section>

        <p className="note">
          {read} of {GMS.length} teams read for {season} · where two keepers share a round the
          charged pick is not in the data, so the row states the round · keeper markers start in
          2010, so a run reaching that far back is a floor rather than a fact.
        </p>
      </Linkify>
    </div>
  );
}
